// Hook: fetch current user's hidden tools list.
// Reads immediately from AuthContext (no API wait), then refreshes via
// /me/tool-visibility on mount AND whenever the logged-in user changes — the
// site-wide HiddenToolsStyleInjector mounts once in Layout (before login), so
// it must re-fetch after login/logout or the Navbar "More" menu stays unfiltered.
//
// The server resolves slugs → canonical slugs (aliases like "ht") and the hrefs
// each tool renders at (e.g. atc → /atc, /atc/b2c, /atc/b2b). Every place a tool
// can appear should use this hook: `hiddenPaths` for links, `isHidden(slug)` for
// widgets that aren't a plain <a href>.
import { useEffect, useState } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

const fromSlugs = (slugs) => ({
  slugs,
  paths: slugs.map((s) => `/${s}`),
});

export function useHiddenTools() {
  const auth = useAuth();
  const userId = auth?.user?.id || null;
  const initialSlugs = Array.isArray(auth?.user?.hidden_tools) ? auth.user.hidden_tools : [];
  const [state, setState] = useState(() => fromSlugs(initialSlugs));
  const [loaded, setLoaded] = useState(initialSlugs.length > 0);

  useEffect(() => {
    let cancelled = false;
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
    if (!token || !userId) { setState(fromSlugs([])); setLoaded(true); return; }
    api.get('/me/tool-visibility')
      .then((r) => {
        if (cancelled) return;
        const slugs = r.data.hidden_slugs || r.data.hidden_tools || [];
        setState({ slugs, paths: r.data.hidden_paths || fromSlugs(slugs).paths });
        setLoaded(true);
      })
      .catch(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, [userId]);

  return {
    hidden: state.slugs,
    hiddenPaths: state.paths,
    loaded,
    isHidden: (slug) => state.slugs.includes(slug),
  };
}

// Kept for backward-compat (called elsewhere on admin save). No-op now.
export function clearHiddenToolsCache() {}
